import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import format from 'string-template';
import { observer } from 'mobx-react';
import {
  Alert,
  Divider,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import useToast from '../../../../../../hooks/useToast';
import AppCustomCheckin, { DEFAULT_TAG } from '../../../../../../Model/AppCustomCheckin';
import { resolveCheckInTemplateTag } from '../../../../../../utils/checkInTemplate';
import {
  InfoContainer,
  StyledTitle,
  StyledSubTitle,
  StyledAccordion,
  StyledArrowIcon,
  SectionContent,
} from '../../styles';
import { accordionId } from '../../utils';

import { InfoIconWrapper, ReceivesRow } from './styles';
import texts from './texts.json';

const { CHECK_IN_TEMPLATE_ACCORDION } = accordionId;

// Same body size as the rest of the profile sections (StyledSubTitle), instead of MUI's 16px.
const selectSx = { fontSize: 14 };

const templateName = (tag) => (tag === DEFAULT_TAG ? texts.defaultName : tag);

const CheckInTemplateSection = ({
  userDoc,
  handleExpand,
  expanded,
  isQAP,
}) => {
  const [templateTags, setTemplateTags] = useState(null);
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  const coachId = userDoc.resolvedCoachId;

  // Tags of this coach's templates that carry at least one question: the ones a client can receive.
  useEffect(() => {
    const load = async () => {
      try {
        const collection = await AppCustomCheckin.getCheckinDocsByCoachId(coachId);
        if (isComponentMountedRef.current) {
          setTemplateTags(
            collection.docs
              .filter((doc) => doc.tag !== DEFAULT_TAG && doc.checkInQuestions.length > 0)
              .map((doc) => doc.tag),
          );
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };
    if (coachId) {
      load();
    }
  }, [coachId, isComponentMountedRef]);

  const resolved = useMemo(() => (
    templateTags && resolveCheckInTemplateTag({
      selectedTag: userDoc.checkInTemplateTag,
      clientTags: userDoc.customTags,
      templateTags,
    })
  ), [templateTags, userDoc.checkInTemplateTag, userDoc.customTags]);

  const handleChange = useCallback(async (event) => {
    try {
      await userDoc.updateCheckInTemplateTag(event.target.value);
    } catch (error) {
      Sentry.captureException(error);
      showToast(texts.saveFailed, { error: true });
    }
  }, [userDoc, showToast]);

  if (!resolved) {
    return null;
  }

  const {
    tag, candidates, isChosen, isTagNameFallback,
  } = resolved;

  /*
    Only a client with several templates has something to choose between. The exception is a pin on
    Default over a template the client still carries: it is kept on purpose when tags change, and it
    keeps that client from their template, so the coach has to be able to see it and undo it.
  */
  const isDefaultOverridingTemplate = isChosen && tag === DEFAULT_TAG && candidates.length > 0;
  if (candidates.length < 2 && !isDefaultOverridingTemplate) {
    return null;
  }

  return (
    <>
      <Divider flexItem />
      <StyledAccordion
        key={texts.title}
        title={<StyledTitle>{texts.title}</StyledTitle>}
        expanded={expanded === CHECK_IN_TEMPLATE_ACCORDION || !isQAP}
        onChange={handleExpand(CHECK_IN_TEMPLATE_ACCORDION)}
        expandIcon={isQAP && <StyledArrowIcon />}
        preventRotation={!isQAP}
        disableGutters
      >
        <InfoContainer>
          <SectionContent>
            <Select
              size="small"
              fullWidth
              displayEmpty
              sx={selectSx}
              value={isChosen ? tag : ''}
              onChange={handleChange}
              inputProps={{ 'aria-label': texts.label }}
              renderValue={(value) => (value ? templateName(value) : texts.notChosen)}
            >
              <MenuItem value={DEFAULT_TAG} sx={selectSx}>{texts.defaultName}</MenuItem>
              {candidates.map((candidate) => (
                <MenuItem key={candidate} value={candidate} sx={selectSx}>{candidate}</MenuItem>
              ))}
            </Select>
            <ReceivesRow>
              <StyledSubTitle>{format(texts.receives, { template: templateName(tag) })}</StyledSubTitle>
              <Tooltip title={texts.appUpdateNote} placement="top" arrow>
                <InfoIconWrapper tabIndex={0}>
                  <InfoOutlinedIcon fontSize="small" />
                </InfoIconWrapper>
              </Tooltip>
            </ReceivesRow>
            {isTagNameFallback && (
              <Alert severity="warning">{format(texts.fallbackWarning, { template: tag })}</Alert>
            )}
          </SectionContent>
        </InfoContainer>
      </StyledAccordion>
    </>
  );
};

CheckInTemplateSection.propTypes = {
  userDoc: PropTypes.object.isRequired,
  handleExpand: PropTypes.func.isRequired,
  expanded: PropTypes.string.isRequired,
  isQAP: PropTypes.bool.isRequired,
};

export default observer(CheckInTemplateSection);
